import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight, Activity, PersonStanding, Sun, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

export default function Services() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Our Services | Woods Spine & Wellness | Longview TX"
        description="Explore our advanced therapies including SoftWave TRT, Red Light Therapy, DRX9000 Spinal Decompression, and ChiroThin Weight Loss in Longview, TX."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png"
        canonical="https://woodsspine.com/services"
      />
      <Navbar />
      <main id="main-content" className="flex-grow pt-24 pb-16">
        {/* Header Section */}
        <section className="container mx-auto px-4 mb-20 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
            Woods Spine and Wellness: <br />
            <span className="text-accent">Improve Your Health and Wellness in Longview, TX</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Services offered by Woods Spine and Wellness
          </p>
        </section>

        {/* Services List */}
        <section className="container mx-auto px-4 mb-24 space-y-24">
          
          {/* ChiroThin Weight Loss */}
          <Link to="/longview-chirothin-weight-loss" className="block group">
            <div className="grid lg:grid-cols-2 gap-12 items-center hover:bg-secondary/10 p-4 sm:p-8 rounded-3xl transition-colors -mx-4 sm:-mx-8">
              <div className="space-y-8">
                <div>
                  <img 
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/6e4569db-806e-4ded-8d4e-b1934a8e917a.png&w=1280&output=webp&q=80" 
                    alt="ChiroThin Weight Loss" 
                    fetchPriority="high"
                    decoding="async"
                    className="w-full max-w-md mx-auto rounded-xl shadow-lg mb-8 aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <h2 className="text-3xl font-bold text-primary mb-4 group-hover:text-accent transition-colors">Transform Your Body with <span className="text-accent">ChiroThin™</span></h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Experience the premier weight loss solution designed exclusively for patients seeking transformative results. The ChiroThin Weight Loss Program is a doctor-supervised program that combines a specific blend of amino acids, vitamins, and cell salts with a customized diet plan.
                  </p>
                </div>

                <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg-accent text-white group-hover:bg-accent/90 h-10 px-4 py-2">
                  Learn more about ChiroThin <ChevronRight className="ml-2 h-4 w-4" />
                </div>
              </div>

              <div>
                <Card className="border-0 shadow-xl bg-secondary/20 group-hover:bg-secondary/30 transition-colors">
                  <CardContent className="p-8 space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-3 flex items-center"><Activity className="mr-2 h-6 w-6 text-accent"/> How It Works</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        The proprietary ChiroThin formula aids in fatty acid transportation, fatty acid metabolism, blood sugar stabilization, and increasing metabolism and detoxification. When combined with our specific blend and amounts of low glycemic index/anti-inflammatory foods, your body converts stored fat into energy.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-primary mb-4 border-b pb-4">Benefits of the Program</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Doctor Supervised:</strong> Weekly visits to ensure safe and effective weight loss.</span>
                        </li>
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Real Food:</strong> No artificial shakes or pre-packaged meals to buy.</span>
                        </li>
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Sustainable Results:</strong> Learn healthy habits for long-term success.</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Link>

          <div className="border-t border-border"></div>

          {/* DRX9000 */}
          <Link to="/longview-spinal-decompression" className="block group">
            <div className="grid lg:grid-cols-2 gap-12 items-center hover:bg-secondary/10 p-4 sm:p-8 rounded-3xl transition-colors -mx-4 sm:-mx-8">
              <div className="space-y-8">
                <div>
                  <img 
                    src="https://wsrv.nl/?url=https://www.excitemedical.com/wp-content/uploads/2024/11/drx9000-lumbar-numbered-graphic.png&w=1280&output=webp&q=80" 
                    alt="DRX9000 Machine" 
                    loading="lazy"
                    decoding="async"
                    className="w-full max-w-md mx-auto rounded-xl shadow-lg mb-8 bg-white p-4 transition-transform duration-500 group-hover:scale-105"
                  />
                  <h2 className="text-3xl font-bold text-primary mb-4 group-hover:text-accent transition-colors">The DRX9000® is the Worlds Most Advanced <span className="text-accent">Non-Surgical Treatment</span></h2>
                  <p className="text-muted-foreground leading-relaxed">
                    I'm Dr. Tucker Woods, creator of Woods Spine and Wellness, and I'm excited to unveil the revolutionary DRX9000 Spinal Decompression treatment to East Texas. Say farewell to invasive procedures and reliance on pain medication—welcome a future of natural, powerful pain relief!
                  </p>
                </div>

                <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg-accent text-white group-hover:bg-accent/90 h-10 px-4 py-2">
                  Learn more about Spinal Decompression <ChevronRight className="ml-2 h-4 w-4" />
                </div>
              </div>

              <div>
                <Card className="border-0 shadow-xl bg-secondary/20 group-hover:bg-secondary/30 transition-colors">
                  <CardContent className="p-8 space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-3 flex items-center"><PersonStanding className="mr-2 h-6 w-6 text-accent"/> Conditions Treated</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        The DRX9000® True Non-Surgical Spinal Decompression® treatment is specifically engineered to provide relief of pain, sciatica and other symptoms associated with Herniated Discs, Bulging Discs, Degenerative Disc Disease and protruding discs.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-primary mb-3 flex items-center"><PersonStanding className="mr-2 h-6 w-6 text-accent"/> How It Works</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        DRX9000® system works on the injuries that happen due to nerve compression by applying equal amount of pressure on the vertebrate columns, by stretching the spine, this creates a gap in between the discs, and with improved flow of nutrients through both the vertebrate columns of spine the injuries heal slowly and steadily.
                      </p>
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-sm text-muted-foreground italic">
                        The DRX9000® is the only system available in its field to utilize a servo motor; in doing so, the DRX9000® contains nested closed-loop feedback that ensures that the therapy stays true to each patient's logarithmic spinal decompression treatment.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Link>

          <div className="border-t border-border"></div>

          {/* Red Light Therapy */}
          <Link to="/longview-red-light-therapy" className="block group">
            <div className="grid lg:grid-cols-2 gap-12 items-center hover:bg-secondary/10 p-4 sm:p-8 rounded-3xl transition-colors -mx-4 sm:-mx-8">
              <div className="order-2 lg:order-1 space-y-8">
                <div>
                  <img 
                    src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/attachments/e3fefda6-4541-41e2-b1b1-3631721c72b4.png&w=1280&output=webp&q=80" 
                    alt="Dahlia Red Light Therapy Bed" 
                    loading="lazy"
                    decoding="async"
                    className="w-full max-w-md object-cover rounded-xl shadow-lg mx-auto lg:mx-0 mb-8 aspect-[4/3] transition-transform duration-500 group-hover:scale-105"
                  />
                  <h2 className="text-3xl font-bold text-primary mb-4 group-hover:text-accent transition-colors">Groundbreaking <span className="text-accent">Dahlia Red Light</span> Body Contouring</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Experience the incredible benefits of our cutting-edge technology with just one treatment that burns 500 calories, equivalent to a full hour of intense workout! This quick and efficient session promotes full body fat loss, reduces cellulite, offers anti-aging benefits, and decreases inflammation.
                  </p>
                </div>

                <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg-accent text-white group-hover:bg-accent/90 h-10 px-4 py-2">
                  Learn more about Red Light Therapy <ChevronRight className="ml-2 h-4 w-4" />
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <Card className="border-0 shadow-xl bg-secondary/20 group-hover:bg-secondary/30 transition-colors">
                  <CardContent className="p-8 space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-3 flex items-center"><Sun className="mr-2 h-6 w-6 text-accent"/> How It Works</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        The Dahlia Red Light Body Contouring Technology uses advanced red light wavelengths that penetrate the skin, targeting fat cells and stimulating your body's natural processes. The red light energy helps to break down stored fat, which is then naturally eliminated by your body.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-primary mb-4 border-b pb-4">Benefits of Therapy</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Non-Invasive and Painless:</strong> No needles, no surgery, and no downtime.</span>
                        </li>
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Effective Fat Reduction:</strong> Target stubborn areas that resist diet and exercise.</span>
                        </li>
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Skin Tightening:</strong> Improve skin elasticity and reduce the appearance of cellulite.</span>
                        </li>
                        <li className="flex items-start text-sm text-muted-foreground">
                          <CheckCircle2 className="h-5 w-5 text-accent mr-3 shrink-0" />
                          <span><strong>Quick and Convenient:</strong> Each session takes just 20-30 minutes.</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Link>

          <div className="border-t border-border"></div>

          {/* SoftWave TRT */}
          <Link to="/longview-softwave-pain-management-tissue-regeneration" className="block group">
            <div className="grid lg:grid-cols-2 gap-12 items-center hover:bg-secondary/10 p-4 sm:p-8 rounded-3xl transition-colors -mx-4 sm:-mx-8">
              <div className="order-2 lg:order-1 space-y-8">
                <div>
                  <img 
                    src="https://softwavetrt.com/wp-content/uploads/2026/02/softwave099-1.png" 
                    alt="SoftWave TRT Device" 
                    loading="lazy"
                    decoding="async"
                    className="w-full max-w-md object-contain mb-8 rounded-xl bg-white p-4 shadow-sm transition-transform duration-500 group-hover:scale-105"
                  />
                  <h2 className="text-3xl font-bold text-primary mb-4 group-hover:text-accent transition-colors">Say Goodbye to <span className="text-accent">Invasive Treatments</span></h2>
                  <p className="text-muted-foreground leading-relaxed">
                    I'm Dr. Tucker Woods, founder of Woods Spine and Wellness, and I couldn't be more thrilled to introduce groundbreaking SoftWave TRT (Tissue Regeneration Technology) to East Texas. Say goodbye to invasive treatments and pain medication—welcome the future of natural, effective healing!
                  </p>
                </div>
                
                <div>
                  <h3 className="text-2xl font-semibold text-primary mb-3">What is SoftWave TRT?</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    SoftWave TRT OrthoGold 350 is not just another treatment; it's a paradigm shift in regenerative medicine. Imagine harnessing the power of supersonic sound waves to tackle inflammation, increase range of motion, stimulate stem cell production, and promote the formation of new blood vessels in the damaged area—all without causing any tissue damage.
                  </p>
                </div>

                <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg-accent text-white group-hover:bg-accent/90 h-10 px-4 py-2">
                  Learn more about SoftWave TRT <ChevronRight className="ml-2 h-4 w-4" />
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <Card className="border-0 shadow-xl bg-secondary/20 group-hover:bg-secondary/30 transition-colors">
                  <CardContent className="p-8 space-y-6">
                    <h3 className="text-2xl font-bold text-primary mb-6 border-b pb-4">Key Benefits of SoftWave Therapy</h3>
                    
                    <div>
                      <h4 className="font-semibold text-primary mb-2 flex items-center"><Activity className="mr-2 h-5 w-5 text-accent"/> Decrease Inflammation</h4>
                      <p className="text-sm text-muted-foreground">SoftWave's innovative supersonic sound waves naturally bring down inflammation, helping alleviate pain.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-primary mb-2 flex items-center"><Activity className="mr-2 h-5 w-5 text-accent"/> Increase Range of Motion</h4>
                      <p className="text-sm text-muted-foreground">Feel more flexible as SoftWave TRT enhances your range of movement in affected areas.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-primary mb-2 flex items-center"><Activity className="mr-2 h-5 w-5 text-accent"/> Stimulate Stem Cell Production</h4>
                      <p className="text-sm text-muted-foreground">This technology encourages your body to repair itself by stimulating stem cell production.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-primary mb-2 flex items-center"><Activity className="mr-2 h-5 w-5 text-accent"/> Enhance Angiogenesis</h4>
                      <p className="text-sm text-muted-foreground">Experience improved healing through the increase in local blood flow in damaged areas.</p>
                    </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-semibold text-primary mb-2">Clinically Proven and FDA-Cleared</h4>
                      <p className="text-sm text-muted-foreground">For over a decade, this treatment has seen resounding success in Europe. With its recent FDA clearance, SoftWave TRT is now being adopted by top U.S. medical institutions and professional sports teams.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Link>

        </section>
      </main>
      <Footer />
    </div>
  );
}
